import { getCountries, CountryCode } from "libphonenumber-js";

export interface LocalizedCountryOption {
  code: CountryCode;
  label: string;
}

// libphonenumber-js's country list is calling-code regions, not strictly
// ISO 3166-1 alpha-2 — it includes a couple of dependent-territory codes
// (Ascension Island, Tristan da Cunha) that share the UK's country calling
// code but were never assigned ISO 3166-1 codes of their own, plus "XK"
// (Kosovo), a user-assigned code with no ISO 3166-1 alpha-2 assignment.
// Excluded here to keep the field's closed list to actual ISO 3166-1
// alpha-2 entries.
// Exported so any other consumer of libphonenumber-js's country list (e.g.
// PhoneControl's calling-code dropdown) can stay consistent with this list —
// a value that resolves to one of these codes must never be treated as
// selectable anywhere this exclusion applies, or a selection UI keyed off a
// filtered options list silently can't find its own selected value.
export const NON_ISO_3166_1_CODES = new Set<string>(["AC", "TA", "XK"]);

// The country field is a closed ISO 3166-1 alpha-2 list (never free text).
// libphonenumber-js already ships that list as a side effect of phone-number
// parsing, so it doubles as the country source with no new dependency —
// Intl.DisplayNames (also built in) renders each code in the viewer's locale.
export const getLocalizedCountryOptions = (
  locale: string
): LocalizedCountryOption[] => {
  let displayNames: Intl.DisplayNames | undefined;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    displayNames = undefined;
  }

  return getCountries()
    .filter((code: CountryCode) => !NON_ISO_3166_1_CODES.has(code))
    .map((code: CountryCode) => ({
      code,
      label: displayNames?.of(code) ?? code
    }))
    .sort((firstOption, secondOption) =>
      firstOption.label.localeCompare(secondOption.label)
    );
};

export interface CountrySelectOption {
  id: string;
  displayName: string;
}

// Built once per locale: form and detail builders call this on every render.
const countryOptionsByLocale = new Map<string, LocalizedCountryOption[]>();

const getCachedCountryOptions = (locale: string): LocalizedCountryOption[] => {
  let options = countryOptionsByLocale.get(locale);
  if (!options) {
    options = getLocalizedCountryOptions(locale);
    countryOptionsByLocale.set(locale, options);
  }
  return options;
};

// Select options for a country field. Values saved before the field became a
// dropdown are free text ("United States"), not codes — keep such a value as
// its own option so it still shows instead of rendering an empty selection.
export const getCountrySelectOptions = (
  locale: string,
  currentValue?: unknown
): CountrySelectOption[] => {
  const options = getCachedCountryOptions(locale).map(({ code, label }) => ({
    id: code as string,
    displayName: label
  }));
  // Matched against the stored value as-is, so the select can find it.
  const legacyValue = typeof currentValue === "string" ? currentValue : "";
  if (
    legacyValue.trim() &&
    !options.some((option) => option.id === legacyValue)
  ) {
    options.push({ id: legacyValue, displayName: legacyValue.trim() });
  }
  return options;
};

// The display name for a stored country: the localized name for a code, the
// value itself for free text saved before the field became a dropdown.
export const getCountryDisplayName = (value: string, locale: string): string =>
  getCachedCountryOptions(locale).find((option) => option.code === value)
    ?.label ?? value;
